import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Circle, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Maximize2, ArrowLeft, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

// Fix default icon issue with Vite/Webpack
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

const DefaultIcon = L.icon({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

interface ProLocationMapProps {
  latitude: number;
  longitude: number;
  /** Service radius in km */
  radiusKm: number;
  className?: string;
  /** When true, clicking the map opens a fullscreen dialog */
  expandable?: boolean;
}

const FitBounds = ({ lat, lng, radiusKm }: { lat: number; lng: number; radiusKm: number }) => {
  const map = useMap();
  useEffect(() => {
    const radiusMeters = radiusKm * 1000;
    const center = L.latLng(lat, lng);
    const bounds = center.toBounds(radiusMeters * 2.4);
    map.fitBounds(bounds, { padding: [10, 10] });
  }, [map, lat, lng, radiusKm]);
  return null;
};

const MapBody = ({
  latitude,
  longitude,
  radiusKm,
  scrollWheel,
  interactive,
}: {
  latitude: number;
  longitude: number;
  radiusKm: number;
  scrollWheel: boolean;
  interactive: boolean;
}) => (
  <MapContainer
    center={[latitude, longitude]}
    zoom={12}
    scrollWheelZoom={scrollWheel}
    dragging={interactive}
    touchZoom={interactive}
    doubleClickZoom={interactive}
    boxZoom={interactive}
    keyboard={interactive}
    style={{ height: "100%", width: "100%" }}
  >
    <TileLayer
      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
    />
    <Circle
      center={[latitude, longitude]}
      radius={radiusKm * 1000}
      pathOptions={{
        color: "hsl(290, 100%, 60%)",
        fillColor: "hsl(320, 100%, 65%)",
        fillOpacity: 0.25,
        weight: 2,
        opacity: 0.7,
      }}
    />
    <Marker position={[latitude, longitude]} />
    <FitBounds lat={latitude} lng={longitude} radiusKm={radiusKm} />
  </MapContainer>
);

const ProLocationMap = ({ latitude, longitude, radiusKm, className, expandable = true }: ProLocationMapProps) => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  return (
    <>
      <div
        className={`relative h-full w-full max-w-full overflow-hidden ${expandable ? "cursor-zoom-in group" : ""}`}
        onClick={expandable ? () => setOpen(true) : undefined}
      >
        <div className={className} style={{ height: "100%", width: "100%", pointerEvents: expandable ? "none" : "auto" }}>
          <MapBody latitude={latitude} longitude={longitude} radiusKm={radiusKm} scrollWheel={false} interactive={!isMobile && !expandable} />
        </div>
        {expandable && (
          <div className="absolute top-2 right-2 z-[400] bg-background/90 backdrop-blur-sm rounded-full p-2 shadow-md opacity-80 group-hover:opacity-100 transition-opacity pointer-events-none">
            <Maximize2 className="w-4 h-4 text-foreground" />
          </div>
        )}
      </div>

      {expandable && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="w-[calc(100vw-1rem)] max-w-5xl h-[min(78dvh,760px)] max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-1rem)] p-0 overflow-hidden rounded-xl sm:rounded-2xl">
            <div className="relative w-full h-full">
              <MapBody latitude={latitude} longitude={longitude} radiusKm={radiusKm} scrollWheel={!isMobile} interactive={true} />
              <Button
                type="button"
                size="icon"
                variant="secondary"
                aria-label="Retour"
                onClick={() => setOpen(false)}
                className="absolute top-3 left-3 z-[500] rounded-full shadow-lg bg-background/95 backdrop-blur-sm hover:bg-background"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="secondary"
                aria-label="Fermer"
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 z-[500] rounded-full shadow-lg bg-background/95 backdrop-blur-sm hover:bg-background"
              >
                <X className="w-5 h-5" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setOpen(false)}
                className="absolute left-1/2 bottom-[calc(env(safe-area-inset-bottom)+12px)] z-[500] -translate-x-1/2 rounded-full shadow-lg bg-background/95 backdrop-blur-sm hover:bg-background sm:hidden"
              >
                Fermer la carte
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default ProLocationMap;
